import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import StatCard from '../components/dashboard/StatCard';
import { useProjects } from '../hooks/useProjects';
import { useQuery } from '@tanstack/react-query';
import chatbotService from '../api/chatbotService';
import PermissionWrapper from '../components/ui/PermissionWrapper';
import { OPENAI_CHAT_MODEL_OPTIONS } from '../constants/openaiModels';

const Dashboard = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const { useAllProjects, useCreateProject } = useProjects();
  const { data: projectsData, isLoading: isProjectsLoading, error: projectsError, refetch } = useAllProjects();
  const createProjectMutation = useCreateProject();
  
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    clientName: '',
    model: 'gpt-4o',
    secondaryModel: 'gpt-4o-mini'
  });
  
  const [formErrors, setFormErrors] = useState({});
  
  const projects = projectsData || [];

  // Get current month and year for stats
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentMonthName = currentDate.toLocaleString('default', { month: 'short' });
  const currentYear = currentDate.getFullYear();

  // Fetch dashboard stats
  const { data: dashboardStats, isLoading: isStatsLoading } = useQuery({
    queryKey: ['dashboardStats', currentYear, currentMonth],
    queryFn: async () => {
      // This fetches aggregate stats for all projects
      const response = await chatbotService.generateBillingReport(null, currentYear, currentMonth);
      return response;
    },
    enabled: !isProjectsLoading && projects.length > 0,
  });

  // Format currency and numbers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  const formatNumber = (num) => {
    return num ? num.toLocaleString() : '0';
  };

  // Create stats array with real data
  const stats = [
    { 
      title: 'Total Projects', 
      value: projects.length || 0, 
      icon: () => <span className="stat-emoji">🤖</span>,
      description: `Active chatbot projects` 
    },
    { 
      title: 'Chat Sessions', 
      value: isStatsLoading ? '...' : formatNumber(dashboardStats?.sessionCount || 0), 
      icon: () => <span className="stat-emoji">💬</span>,
      description: `For ${currentMonthName} ${currentYear}` 
    },
    { 
      title: 'API Calls', 
      value: isStatsLoading ? '...' : formatNumber(dashboardStats?.totalApiCalls || 0), 
      icon: () => <span className="stat-emoji">🔌</span>,
      description: `For ${currentMonthName} ${currentYear}` 
    },
    { 
      title: 'Total Cost', 
      value: isStatsLoading ? '...' : formatCurrency(dashboardStats?.totalCost || 0), 
      icon: () => <span className="stat-emoji">💰</span>,
      description: `For ${currentMonthName} ${currentYear}` 
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProject(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const validateForm = () => {
    const errors = {};
    if (!newProject.name.trim()) {
      errors.name = 'Project name is required';
    }
    if (!newProject.clientName.trim()) {
      errors.clientName = 'Client name is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleCreateProject = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await createProjectMutation.mutateAsync(newProject);
      setNewProject({
        name: '',
        description: '',
        clientName: '',
        model: 'gpt-4o',
        secondaryModel: 'gpt-4o-mini'
      });
      setShowCreateForm(false);
      refetch(); // Refresh the projects list
    } catch (error) {
      console.error('Error creating project:', error);
      setFormErrors({
        submit: 'Failed to create project. Please try again.'
      });
    }
  };

  return (
    <Layout>
      <div className="dashboard">
        <h1 className="page-title">Dashboard</h1>
        
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              change={stat.change}
              changeType={stat.changeType}
              description={stat.description}
              isLoading={isStatsLoading && index > 0}
            />
          ))}
        </div>

        <div className="mt-8">
          <div className="dashboard-header">
            <h2 className="text-xl font-semibold">Your Chatbot Projects</h2>
            <PermissionWrapper section="createProjects">
              <button 
                className="btn btn-primary text-smd"
                onClick={() => setShowCreateForm(!showCreateForm)}
              >
                {showCreateForm ? 'Cancel' : 'Create New Project'}
              </button>
            </PermissionWrapper>
          </div>

          {showCreateForm && (
            <div className="card p-6 mt-4">
              <h2 className="text-xl font-medium mb-4">Create New Project</h2>
              <form onSubmit={handleCreateProject}>
                <div className="form-group mb-4">
                  <label htmlFor="name" className="form-label">
                    Project Name*
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className={`form-input ${formErrors.name ? 'border-red-500' : ''}`}
                    value={newProject.name}
                    onChange={handleInputChange}
                    placeholder="Enter project name"
                  />
                  {formErrors.name && (
                    <p className="form-error">{formErrors.name}</p>
                  )}
                </div>

                <div className="form-group mb-4">
                  <label htmlFor="clientName" className="form-label">
                    Client Name*
                  </label>
                  <input
                    type="text"
                    id="clientName"
                    name="clientName"
                    className={`form-input ${formErrors.clientName ? 'border-red-500' : ''}`}
                    value={newProject.clientName}
                    onChange={handleInputChange}
                    placeholder="Enter client name"
                  />
                  {formErrors.clientName && (
                    <p className="form-error">{formErrors.clientName}</p>
                  )}
                </div>

                <div className="form-group mb-4">
                  <label htmlFor="description" className="form-label">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows="3"
                    className="form-input"
                    value={newProject.description}
                    onChange={handleInputChange}
                    placeholder="Enter project description"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="form-group">
                    <label htmlFor="model" className="form-label">
                      Primary LLM Model
                    </label>
                    <select
                      id="model"
                      name="model"
                      className="form-input"
                      value={newProject.model}
                      onChange={handleInputChange}
                    >
                      {OPENAI_CHAT_MODEL_OPTIONS.map((model) => (
                        <option key={model.value} value={model.value}>{model.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="secondaryModel" className="form-label">
                      Secondary LLM Model
                    </label>
                    <select
                      id="secondaryModel"
                      name="secondaryModel"
                      className="form-input"
                      value={newProject.secondaryModel}
                      onChange={handleInputChange}
                    >
                      {OPENAI_CHAT_MODEL_OPTIONS.map((model) => (
                        <option key={model.value} value={model.value}>{model.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {formErrors.submit && (
                  <p className="form-error mb-4">{formErrors.submit}</p>
                )}

                <div className="flex justify-end">
                  <button
                    type="button"
                    className="btn btn-secondary mr-2 text-smd"
                    onClick={() => setShowCreateForm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary text-smd"
                    disabled={createProjectMutation.isLoading}
                  >
                    {createProjectMutation.isLoading ? 'Creating...' : 'Create Project'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {isProjectsLoading ? (
            <div className="card p-4 text-center mt-4">
              <p>Loading projects...</p>
            </div>
          ) : projectsError ? (
            <div className="card p-4 text-center mt-4">
              <p>Error loading projects. Please try again.</p>
            </div>
          ) : projects.length === 0 ? (
            <div className="card p-6 mt-4">
              <h3 className="text-lg font-medium mb-2">No projects yet</h3>
              <PermissionWrapper 
                section="createProjects"
                fallback={<p className="mb-4">Contact your administrator to get access to projects.</p>}
              >
                <p className="mb-4">Create your first chatbot project to get started.</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowCreateForm(true)}
                >
                  Create Your First Project
                </button>
              </PermissionWrapper>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {projects.map((project) => (
                <div key={project._id} className="card p-4">
                  <h3 className="text-lg font-medium mb-2">{project.name}</h3>
                  <p className="mb-4 text-sm">{project.description || 'No description provided'}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                      {project.primaryLLM?.model || project.model || 'GPT-4o'}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                      {project.stats?.totalSessions || project.sessions || 0} Sessions
                    </span>
                    {project.clientName && (
                      <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                        Client: {project.clientName}
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between">
                    <Link to={`/projects/${project._id}`} className="btn btn-secondary">
                      View Details
                    </Link>
                    <Link to={`/projects/${project._id}/simulator`} className="btn btn-primary">
                      Test Chatbot
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4">Quick Start Guide</h2>
          <div className="card p-6">
            <ol className="list-decimal pl-6 space-y-4">
              <li>
                <strong>Create a Project</strong> - Set up a new chatbot project for your client
              </li>
              <li>
                <strong>Configure API Endpoints</strong> - Add the APIs your chatbot needs to access
              </li>
              <li>
                <strong>Build Prompts</strong> - Design system prompts for the decision-making and response-generation LLMs
              </li>
              <li>
                <strong>Add Knowledge Base</strong> - Upload documents to the vector database for information retrieval
              </li>
              <li>
                <strong>Test Your Chatbot</strong> - Use the simulator to test your chatbot's responses
              </li>
              <li>
                <strong>Integrate SDK</strong> - Add the chatbot to your client's website or application
              </li>
            </ol>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard; 